import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import HorizontalScroll from '@components/HorizontalScroll';

import BottomActions from '@features/crm/sales-funel/components/BottomActions';
import RevenueSummary from '@features/crm/sales-funel/components/RevenueSummary';
import {
	allFilterPages,
	generateSalesFunnelDashboardParams,
	salesFunnelFilterFields
} from '@features/filter/static/filterUtil';
import FilterComponent from '@features/filter/v2/FilterComponent';
import CloseOrderModal from '@features/crm/sales-funel/components/CloseOrderModal';

import {
	storeCRMProducts,
	toggleCRMLeftSidePanel,
	toggleCRMSalesFunnelSummary
} from '@store/actions/crm/CRMCommonAction';
import { clearGlobalFilter, storeInitialItemInGlobalFilter } from '@store/actions/FilterAction';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import { fetchDependentFilterList } from '@services/commonService';
import {
	closeSalesFunnelStyle,
	findAllSalesFunnel,
	findGroupWiseCounts,
	updateSalesFunnelOrderDate
} from '@services/crmService';

import {
	_getKey,
	changeDateFormat,
	convertNumberToUSFormat,
	getNumberUnit,
	getProductStatusWiseColor,
	renderSetImages,
	STATIC_SET_CONSTANTS
} from '@utils/Util';

const SalesFunnel = ({ isShowFilter }) => {
	const filterFields = useSelector((store) => store.filterFieldStore);
	const crmStore = useSelector((state) => state.crmCommonStore);
	const selectedCRMProducts = useSelector((state) => state.crmCommonStore.selectedCRMProducts);
	const isLoading = useSelector((store) => store.loaderStore);
	const [isShowTableScroll, setIsShowTableScroll] = useState(false);
	const [editRowIndex, setEditRowIndex] = useState(null);
	const [groupWiseCounts, setGroupWiseCounts] = useState([]);
	const [tableData, setTableData] = useState([]);
	const salesFunnelTableRef = useRef(null);
	const [orderDateValue, setOrderDateValue] = useState(null);
	const [confirmationInfo, setConfirmationInfo] = useState({});
	const [openConfirmation, setOpenConfirmation] = useState(false);
	const [showHorizontalScroll, setShowHorizontalScroll] = useState(false);
	const [enableApiCall, setEnableApiCall] = useState(false);
	const [selectedReason, setSelectedReason] = useState('Buyer cancelled');
	const [otherReason, setOtherReason] = useState('');
	const elementRef = useRef(null);
	const { search } = useLocation();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const searchParams = useMemo(() => new URLSearchParams(search), [search]);

	useEffect(() => {
		(async () => {
			let productGroupId = searchParams.get('productGroupId');
			let memberId = searchParams.get('memberId');
			let brandId = searchParams.get('brandId');
			let tmpFilterData = {};
			if (brandId) {
				tmpFilterData['brandIds'] = [
					{
						name: 'brandIds',
						key: parseInt(brandId),
						value: searchParams.get('brandName'),
						identifier: `brandIds_${brandId}`,
						checked: true
					}
				];
			}
			if (productGroupId) {
				tmpFilterData['productGroupIdList'] = [
					{
						name: 'productGroupIdList',
						key: parseInt(productGroupId),
						value: searchParams.get('productGroupName'),
						identifier: `productGroupIdList_${productGroupId}`,
						checked: true
					}
				];
			}
			if (memberId) {
				tmpFilterData['accountManagerIdList'] = [
					{
						name: 'accountManagerIdList',
						key: parseInt(memberId),
						value: searchParams.get('memberName'),
						identifier: `accountManagerIdList_${memberId}`,
						checked: true
					}
				];
			}
			await dispatch(storeInitialItemInGlobalFilter(tmpFilterData));
			setEnableApiCall(true);
		})();
	}, [searchParams]);

	useEffect(() => {
		dispatch(toggleCRMLeftSidePanel(true));
		dispatch(toggleCRMSalesFunnelSummary(true));
		document.addEventListener('scroll', handlePageScroll);
		return () => {
			document.removeEventListener('scroll', handlePageScroll);
		};
	}, []);

	const handlePageScroll = () => {
		if (window.scrollY === 0) {
			setIsShowTableScroll(false);
		}
		if (window.scrollY > 220) {
			setIsShowTableScroll(true);
		}
	};

	useEffect(() => {
		if (tableData.length > 0) {
			setShowHorizontalScroll(true);
		}
	}, [tableData]);

	useEffect(() => {
		if (enableApiCall && isShowFilter) {
			(async () => {
				const wrappedElement = document.getElementById('salesFunnelDashboardTable');
				if (wrappedElement) {
					wrappedElement.scrollTop = 0;
				}
				dispatch(openLoader());
				let params = generateSalesFunnelDashboardParams(filterFields, true);
				await dispatch(fetchDependentFilterList({ type: 'SALES_FUNNEL_DASHBOARD' }, params));
				let sortKey = filterFields.sort?.[0]?.key;
				if (sortKey !== undefined && sortKey !== 'id,desc') {
					params['groupBy'] =
						sortKey === 'keyAccountManager'
							? 'ACCOUNT_MANAGER'
							: sortKey === 'productGroupName'
								? 'PRODUCT_GROUP'
								: 'BRAND';
					const data = await findGroupWiseCounts(params);
					setGroupWiseCounts(data);
				}
				dispatch(closeLoader());
			})();
		}
	}, [filterFields, enableApiCall, isShowFilter]);

	const fetchSalesFunnelTableData = async (page) => {
		try {
			const params = generateSalesFunnelDashboardParams(filterFields, true);
			const responseBody = await findAllSalesFunnel({ page, size: 50, ...params });
			let mergedData = responseBody.data;
			if (responseBody.currentPage > 0) {
				mergedData = [...salesFunnelTableRef.current.data, ...responseBody.data];
			}
			salesFunnelTableRef.current = { ...responseBody, data: mergedData };
			setTableData(mergedData);
		} catch {
			dispatch(closeLoader());
		}
	};

	useEffect(() => {
		if (enableApiCall) {
			(async () => {
				dispatch(openLoader());
				await fetchSalesFunnelTableData(0);
				await dispatch(storeCRMProducts([]));
				dispatch(closeLoader());
			})();
		}
	}, [filterFields, enableApiCall]);

	const getCategoryListFromSet = (product) => {
		let categoryList = product?.setMemberProducts?.map((item) => item.categoryName);
		return [...new Set(categoryList)].join(', ');
	};

	const findSameAcIdCountAndFirstIndex = (array, key) => {
		let countMap = {};
		let firstIndexMap = {};
		array.forEach((item, index) => {
			const keyId = item[key];
			if (Object.prototype.hasOwnProperty.call(countMap, keyId)) {
				countMap[keyId]++;
			} else {
				countMap[keyId] = 1;
				firstIndexMap[keyId] = index;
			}
		});

		let results = {};
		for (let keyId in countMap) {
			if (Object.prototype.hasOwnProperty.call(countMap, keyId)) {
				results[keyId] = {
					sameAcIdCount: countMap[keyId],
					firstIndex: firstIndexMap[keyId],
					keyId: parseInt(keyId)
				};
			}
		}

		return results;
	};

	const handleEditClick = (index) => {
		setEditRowIndex(index);
	};

	const handleDateChange = async () => {
		try {
			const body = {
				expectedOrderDate: orderDateValue.value
			};
			dispatch(openLoader());
			await updateSalesFunnelOrderDate(body, orderDateValue.styleId);
			setEditRowIndex(null);
			const newTableData = [...tableData];
			newTableData[orderDateValue.index].expectedOrderDate = orderDateValue.value;
			setTableData(newTableData);
			setOrderDateValue(null);
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	const renderTableBody = () => {
		let groupRows = false;
		let result = null;
		let sortValue = filterFields.sort?.[0]?.key;
		let keyName = '';
		let isUnmatchedGroupExist = false;
		if (sortValue !== undefined && sortValue !== 'id,desc') {
			keyName =
				sortValue === 'keyAccountManager'
					? 'keyAccountManagerId'
					: sortValue === 'productGroupName'
						? 'productGroupId'
						: 'brandId';
			result = findSameAcIdCountAndFirstIndex(tableData, keyName);
			if ('undefined' in result && result['undefined'].sameAcIdCount > 0) {
				isUnmatchedGroupExist = true;
			}
			groupRows = true;
		}
		let isPrevDataWasOverdue = false;
		return tableData?.map((styleInfo, index) => {
			let isShowTableHeading =
				groupRows &&
				result !== null &&
				(isUnmatchedGroupExist || styleInfo[keyName] === result[styleInfo[keyName]].keyId) &&
				index === result[styleInfo[keyName]].firstIndex;

			let groupInfo = groupWiseCounts?.find((item) => item.id === styleInfo[keyName]);
			let unGroupMessage = null;
			if (keyName === 'keyAccountManagerId' && isUnmatchedGroupExist) {
				unGroupMessage = 'No Account Manager';
			} else if (keyName === 'productGroupId' && isUnmatchedGroupExist) {
				unGroupMessage = 'No Market';
			} else if (keyName === 'brandId' && isUnmatchedGroupExist) {
				unGroupMessage = 'No Brand';
			}
			const reactElements = (
				<React.Fragment key={`sales_funnel_style_${styleInfo.id}_${index}`}>
					{isShowTableHeading && (
						<tr className="summary-row">
							<td colSpan={12} className="bg-gray">
								<h3 className="semibold-16">
									{unGroupMessage === null ? groupInfo?.name : unGroupMessage} (
									{getNumberUnit(groupInfo?.totalNoOfUnit)} styles / $
									{getNumberUnit(groupInfo?.totalValue)} )
								</h3>
							</td>
						</tr>
					)}
					{isShowTableHeading && tableHeading()}
					<tr
						className={`${styleInfo?.isOverdue ? 'row-error bottom-border ' : ''}${
							styleInfo?.designClosedAt ? 'row-disabled ' : ''
						}${styleInfo?.isOverdue && !isPrevDataWasOverdue ? 'top-border ' : ''}`}
					>
						<td className="select-box-column">
							{!styleInfo.orderStatusName && (
								<div className="custom-chekbox">
									<div className="form-group m-0">
										<input
											type="checkbox"
											id={styleInfo.id}
											name="productId"
											//	value={product.id}
											onChange={() => toggleSelect(styleInfo)}
											checked={isChecked(styleInfo.id)}
										/>
										<label htmlFor={styleInfo.id} />
									</div>
								</div>
							)}
						</td>
						<td>
							{styleInfo?.keyAccountMangerName && (
								<p className="regular-14 weight-500 mb-1">
									A/C:{' '}
									<span className="gray_dark_01">{styleInfo?.keyAccountMangerName}</span>
								</p>
							)}
							{styleInfo?.teamMemberList?.length > 0 && (
								<p className="regular-14 weight-500">
									Team:{' '}
									<span className="gray_dark_01">
										{styleInfo?.teamMemberList
											?.map((item) => item.teamMemberName)
											.join(', ')}
									</span>
								</p>
							)}
						</td>
						<td>
							<p className="regular-14 weight-500 mb-1">{styleInfo?.brandName}</p>
							{styleInfo?.setPackStyleFlag !== STATIC_SET_CONSTANTS.SET_PRODUCT && (
								<p className="regular-12">{styleInfo?.productGroupName}</p>
							)}
							{styleInfo?.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT && (
								<p className="regular-12">
									{styleInfo?.setMemberProducts?.[0]?.productGroupName}
								</p>
							)}
						</td>
						<td>
							<div className="d-flex align-items-center">
								{styleInfo?.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT &&
									renderSetImages(
										styleInfo?.featureImageUrl?.map((item) => ({ docUrl: item }))
									)}
								{styleInfo?.setPackStyleFlag !== STATIC_SET_CONSTANTS.SET_PRODUCT && (
									<img
										src={styleInfo?.featureImageUrl?.[0]}
										alt="style"
										className="cell-img"
									/>
								)}
								<div className="ml-2">
									<p className="regular-14 gray_dark_01 mb-1">
										{styleInfo?.productReferenceNumber}
									</p>
									{styleInfo?.setPackStyleFlag !== STATIC_SET_CONSTANTS.SET_PRODUCT && (
										<p className="regular-12">{styleInfo?.categoryName}</p>
									)}
									{styleInfo?.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT && (
										<p className="regular-12">{getCategoryListFromSet(styleInfo)}</p>
									)}
								</div>
							</div>
						</td>
						<td className="status-column quote">
							<span
								className={`status ${getProductStatusWiseColor[styleInfo?.quoteStatusName]}`}
							>
								{styleInfo?.quoteStatusName}
							</span>
							<p className="regular-14 gray_dark_01 mt-2 ml-2">
								{changeDateFormat(styleInfo?.quoteRequiredDate, '', 'MMM DD, YYYY')}
							</p>
						</td>
						<td>
							<p
								className="regular-14 gray_dark_01 cursor-pointer hover-underline"
								onClick={async () => {
									await dispatch(clearGlobalFilter());
									navigate(
										`/costing/${styleInfo.quoteRequestId}?selectedQuote=${styleInfo?.quoteItemId}`
									);
								}}
							>
								{styleInfo?.quoteReferenceNumber}
							</p>
							{styleInfo?.quoteRequestDate && (
								<p className="regular-14 gray_dark_01 mt-2">
									Rqst: {changeDateFormat(styleInfo?.quoteRequestDate, '', 'MMM DD, YYYY')}
								</p>
							)}
						</td>
						<td className="status-column sample">
							<span
								className={`status ${getProductStatusWiseColor[styleInfo?.sampleStatusName]}`}
							>
								{styleInfo?.sampleStatusName}
							</span>
							<p className="regular-14 gray_dark_01 mt-2 ml-2">
								{changeDateFormat(styleInfo?.sampleRequiredDate, '', 'MMM DD, YYYY')}
							</p>
						</td>
						<td>
							<p
								className="regular-14 gray_dark_01 cursor-pointer hover-underline"
								onClick={async () => {
									await dispatch(clearGlobalFilter());
									navigate(
										`/sample/view/${styleInfo.sampleRequestId}?selectedSample=${styleInfo?.sampleItemId}`
									);
								}}
							>
								{styleInfo?.sampleReferenceNumber}
							</p>
							{styleInfo?.sampleRequestDate && (
								<p className="regular-14 gray_dark_01 mt-2">
									Rqst: {changeDateFormat(styleInfo?.sampleRequestDate, '', 'MMM DD, YYYY')}
								</p>
							)}
						</td>
						<td className="status-column po">
							<span
								className={`status ${getProductStatusWiseColor[styleInfo?.orderStatusName]}`}
							>
								{styleInfo?.orderStatusName}
							</span>
							<p className="regular-14 gray_dark_01 mt-2 ml-2">
								{changeDateFormat(styleInfo?.orderStatusUpdatedAt, '', 'MMM DD, YYYY')}
							</p>
						</td>
						<td>
							{styleInfo?.orderReferenceNumber && (
								<p
									className="regular-14 gray_dark_01 cursor-pointer hover-underline"
									onClick={async () => {
										await dispatch(clearGlobalFilter());
										navigate(`/orders/view/${styleInfo.orderId}`);
									}}
								>
									{styleInfo?.orderReferenceNumber}
								</p>
							)}
							{styleInfo?.poReceiveDate && (
								<p className="regular-14 gray_dark_01 mt-2">
									Rqst: {changeDateFormat(styleInfo?.poReceiveDate, '', 'MMM DD, YYYY')}
								</p>
							)}
						</td>
						<td>
							{styleInfo?.totalQuantity > 0 && (
								<p className="gray_dark_01">
									{convertNumberToUSFormat(styleInfo.totalQuantity)}
								</p>
							)}
						</td>
						<td>
							{styleInfo?.totalValue > 0 && (
								<p className="gray_dark_01">
									$
									{getNumberUnit((styleInfo.totalValue * styleInfo.probabilityValue) / 100)}
								</p>
							)}
						</td>
						<td>
							{styleInfo?.expectedMargin > 0 && (
								<p className="gray_dark_01">{styleInfo.expectedMargin.toFixed(2)}%</p>
							)}
						</td>
						<td>
							{styleInfo?.probabilityValue > 0 && (
								<p className="gray_dark_01">{styleInfo.probabilityValue}%</p>
							)}
						</td>
						<td>
							<p className="gray_dark_01">
								{editRowIndex === index ? (
									<input
										type="date"
										value={orderDateValue?.value || styleInfo.expectedOrderDate}
										onChange={(event) =>
											setOrderDateValue({
												value: event.target.value,
												index,
												styleId: styleInfo.id
											})
										}
										onBlur={handleDateChange}
									/>
								) : (
									<>
										{changeDateFormat(styleInfo.expectedOrderDate, '', 'MMM DD, YYYY')}
										<img
											src={`${process.env.PUBLIC_URL}/icons/edit-black-20.svg`}
											className="column-hover-icon ml-2 cursor-pointer"
											alt="edit"
											onClick={() => handleEditClick(index)}
										/>
									</>
								)}
							</p>
						</td>
						<td>{actionsMenu(styleInfo)}</td>
					</tr>
				</React.Fragment>
			);
			isPrevDataWasOverdue = !!styleInfo?.isOverdue;
			return reactElements;
		});
	};

	const actionsMenu = (styleInfo) => {
		return (
			<div className="actions text-right">
				<div className="dropdown">
					<button
						disabled={
							styleInfo?.designClosedAt ||
							styleInfo.probabilityValue === 90 ||
							styleInfo.probabilityValue === 100
						}
						className="btn btn-default"
						type="button"
						id="menu1"
						data-toggle="dropdown"
						aria-expanded="true"
					>
						<img src={`${process.env.PUBLIC_URL}/icons/more_icon.svg`} alt="option" />
					</button>
					<ul
						className="dropdown-menu dropdown-menu-right shadow-lg"
						role="menu"
						aria-labelledby="menu1"
					>
						<li
							onClick={() => {
								setConfirmationInfo({
									id: styleInfo?.id,
									title: 'Are you sure?',
									description: 'you want to close this style?',
									footerType: 'CLOSE'
								});
								setOpenConfirmation(true);
							}}
						>
							<span>Close Style</span>
						</li>
					</ul>
				</div>
			</div>
		);
	};

	const onCloseConfirmationModal = () => {
		setConfirmationInfo({});
		setOpenConfirmation(false);
	};

	const handleTableScroll = () => {
		const wrappedElement = document.getElementById('salesFunnelDashboardTable');
		const { scrollTop, scrollHeight, clientHeight } = wrappedElement;
		const scrollDifference = Math.abs(Math.ceil(scrollHeight - scrollTop) - clientHeight);

		if (scrollDifference <= 5 && !isLoading && scrollTop !== 0) {
			let { totalElements, data, currentPage } = salesFunnelTableRef.current;
			if (totalElements !== data.length) {
				(async () => {
					dispatch(openLoader());
					await fetchSalesFunnelTableData(parseInt(currentPage) + 1);
					dispatch(closeLoader());
				})();
			}
		}
	};
	const getProductsIds = (products = []) => {
		const selectedProduct = products.map((product) => product);
		return selectedProduct;
	};
	const onSelectAll = async (e, products) => {
		if (e.target.checked) {
			// 	const selectedIdList = [...new Set([...selectedProductIds, ...ids])];
			await dispatch(storeCRMProducts(products.filter((item) => !item.orderStatusName)));
		} else {
			// 	const modifiedIds = selectedProductIds.filter((item) => !ids.includes(item));
			await dispatch(storeCRMProducts([]));
		}
	};

	const toggleSelect = async (product) => {
		let tmpSelectedProductIds = [...selectedCRMProducts];
		const productExists = tmpSelectedProductIds.some(
			(selectedProduct) =>
				selectedProduct.id === product.id && selectedProduct.designClosedAt === product.designClosedAt
		);
		if (productExists) {
			tmpSelectedProductIds = selectedCRMProducts.filter(
				(singleProduct) => singleProduct.id !== product.id
			);
		} else {
			tmpSelectedProductIds.push(product);
		}
		// console.log('tmpSelectedProductIds:', tmpSelectedProductIds);
		await dispatch(storeCRMProducts(tmpSelectedProductIds));
	};
	const isChecked = (productId) => {
		return selectedCRMProducts.some((product) => product.id === productId);
	};
	const tableHeading = () => {
		let newId = `salesFunelsAllSelect_${_getKey()}`;
		return (
			<tr className="heading-row">
				<th className="select-box-column">
					<div className="custom-chekbox">
						<div className="form-group m-0">
							<input
								type="checkbox"
								name={newId}
								id={newId}
								onChange={(e) => onSelectAll(e, tableData)}
								checked={
									tableData.length > 0 &&
									getProductsIds(tableData.filter((item) => !item.orderStatusName))
										.length === selectedCRMProducts.length
								}
							/>
							<label htmlFor={newId} />
						</div>
					</div>
				</th>
				<th>Team Member</th>
				<th>Brand Details</th>
				<th>Style</th>
				<th className="costing-status">Costing Status</th>
				<th className="costing-link">Costing Link</th>
				<th className="sample-status">Sample Status</th>
				<th className="sample-link">Sample Link</th>
				<th className="po-status">PO Status</th>
				<th className="po-status">PO Receive Link</th>
				<th>Total Qty</th>
				<th>Potential Value</th>
				<th>Expected Margin</th>
				<th>Probability</th>
				<th>Expected Order Date</th>
				<th>Actions</th>
			</tr>
		);
	};

	const closeStyle = async () => {
		try {
			dispatch(openLoader());
			const params = `?designClosingReason=${selectedReason === 'Other' ? otherReason : selectedReason}`;
			await closeSalesFunnelStyle(confirmationInfo.id, params);
			onCloseConfirmationModal();
			await fetchSalesFunnelTableData(0);
			setSelectedReason('Buyer cancelled');
			setOtherReason('');
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	return (
		<section className="filter-data-container filter-v3-data-container under-inner-tabs">
			<FilterComponent
				filterPageName={allFilterPages.SALES_FUNNEL_LIST}
				fields={salesFunnelFilterFields}
				showFilter={isShowFilter}
				isDependentFilter={true}
			/>
			<div className="data-table w-100 overflow-hidden">
				<RevenueSummary enableApiCall={enableApiCall} />
				{tableData.length === 0 && (
					<div className="alert alert-secondary text-center" role="alert">
						No data found
					</div>
				)}
				{tableData.length > 0 && (
					<div className="sales-funnel-table">
						{showHorizontalScroll && (
							<div className="d-flex justify-content-end">
								<HorizontalScroll elementReference={elementRef.current} />
							</div>
						)}
						<table
							id="salesFunnelDashboardTable"
							className={`table with-border no-hover scroll-x-label scroll-y-label ${
								isShowTableScroll ? 'show-scroll' : ''
							} ${!crmStore?.isShowSalesFunnelSummary ? 'hide-summary' : ''}`}
							onScroll={handleTableScroll}
							ref={elementRef}
						>
							{filterFields.sort?.[0]?.key === undefined && <thead>{tableHeading()}</thead>}
							<tbody>{renderTableBody()}</tbody>
						</table>
					</div>
				)}
			</div>
			{/* {openConfirmation && (
				<ConfirmationModal
					title={confirmationInfo?.title}
					closeModal={onCloseConfirmationModal}
					description={confirmationInfo?.description}
					callback={closeStyle}
					footerType={confirmationInfo?.footerType}
				/>
			)} */}
			{selectedCRMProducts.length > 0 && (
				<BottomActions
					allProducts={getProductsIds(tableData)}
					fetchSalesFunnelTableData={fetchSalesFunnelTableData}
				/>
			)}

			{openConfirmation && (
				<CloseOrderModal
					onCloseModal={() => setOpenConfirmation(false)}
					selectedReason={selectedReason}
					setSelectedReason={setSelectedReason}
					otherReason={otherReason}
					setOtherReason={setOtherReason}
					onSubmit={closeStyle}
				/>
			)}
		</section>
	);
};

export default SalesFunnel;
